import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'
import type { Piece, PieceLocation, PieceLocationType, PieceTimeIndication, PieceType, Price } from '@dhl/pieces'
import { fetch } from '@dhl/fetch'
import { getConfig } from '~/config/index.js'

type OutgoingPiecesApiLocation = {
  name: string
  locationType:
    | 'ADDRESS'
    | 'UNKNOWN'
    | 'PARCEL_SHOP'
    | 'TERMINAL'
    | 'RETURN'
    | 'PARCEL_STATION'
    | 'NEIGHBOUR'
    | 'AGREED_PLACE'
  address: {
    street: string
    houseNumber?: string
    houseNumberSuffix?: string
    postalCode?: string
    city: string
    country: string
  }
}

type OutgoingPiecesApiPiece = {
  type: PieceType
  orderId?: string
  barcode?: string
  printlessCode?: string
  receiver: OutgoingPiecesApiLocation
  destination?: OutgoingPiecesApiLocation
  sender: OutgoingPiecesApiLocation
  timeCreated: string
  receivingTimeIndication: PieceTimeIndication
  status?: string
  category?: string
  intervenable: boolean
  totalPrice: Price
}

const mapLocationType = (
  apiLocationType: OutgoingPiecesApiLocation['locationType'] | undefined
): PieceLocationType | undefined => {
  switch (apiLocationType) {
    case undefined:
      return undefined

    case 'ADDRESS':
      return 'ADDRESS'

    case 'NEIGHBOUR':
      return 'NEIGHBOUR'

    case 'AGREED_PLACE':
      return 'AGREEDPLACE'

    case 'PARCEL_SHOP':
      return 'SERVICEPOINT'

    case 'TERMINAL':
      return 'TERMINAL'

    case 'RETURN':
      return 'RETURN'

    case 'PARCEL_STATION':
      return 'PARCELSTATION'

    default:
      return 'UNKNOWN'
  }
}

const mapLocation = (apiLocation: OutgoingPiecesApiLocation | undefined): PieceLocation | undefined => {
  if (!apiLocation) {
    return undefined
  }
  return {
    name: apiLocation.name?.trim() === '' ? undefined : apiLocation.name,
    locationType: mapLocationType(apiLocation.locationType),
    address: {
      countryCode: apiLocation.address.country,
      postalCode: apiLocation.address.postalCode,
      city: apiLocation.address.city,
      street: apiLocation.address.street,
      houseNumber: apiLocation.address.houseNumber,
      houseNumberSuffix: apiLocation.address.houseNumberSuffix,
      suburb: undefined,
      additionalAddressLine: undefined,
      lines: [],
    },
  }
}

const outgoingPieceToPiece = (apiPiece: OutgoingPiecesApiPiece): Piece => ({
  orderId: apiPiece.orderId,
  // MDP pieces don't have a parcelUuid
  pieceId: '',
  type: 'outgoing',
  barcode: apiPiece.barcode ?? '',
  destination: mapLocation(apiPiece.destination),
  receiver: mapLocation(apiPiece.receiver),
  sender: {
    name: apiPiece.sender.name,
  },
  timeCreated: apiPiece.timeCreated,
  receivingTimeIndication: apiPiece.receivingTimeIndication,
  status: apiPiece.status ?? '',
  category: apiPiece.category,
  intervenable: apiPiece.intervenable,
  intervenableBarcode: null,
  totalPrice: {
    ...apiPiece.totalPrice,
    currency: apiPiece.totalPrice?.currency ?? 'EUR',
  },
  receivedDaysAgo: null,
  returnedDaysAgo: null,
  isReturn: false,
  isRegistered: false,
  printlessCode: apiPiece.printlessCode,
  identifier: apiPiece.barcode || apiPiece.printlessCode || uuidv4(),
  isClimateNeutralDelivery: false,
  undeliverableSinceDaysAgo: null,
  isUndeliverable: false,
  returnable: null,
  hasDeliveryCode: null,
  hasLockerCode: null,
  hasServicePointPickupCode: null,
  source: 'MDP',
})

export const getLegacyOutgoingPiecesQueryKey = (): readonly ['api/orders/sentShipments'] =>
  ['api/orders/sentShipments'] as const

export const getLegacyOutgoingPieces = async (): Promise<Piece[]> => {
  const {
    features: { send },
  } = getConfig()

  const outgoingPieces = await fetch<void, OutgoingPiecesApiPiece[]>({
    method: 'GET',
    url: `/api/orders/sentShipments?max=${send.outgoingPiecesMax}`,
  })

  return outgoingPieces.map(outgoingPieceToPiece)
}

export const useGetLegacyOutgoingPieces = (): UseQueryResult<Piece[]> =>
  useQuery({
    staleTime: 5 * 60 * 1000,
    queryKey: [getLegacyOutgoingPiecesQueryKey()],
    queryFn: async () => getLegacyOutgoingPieces(),
  })
